import { useRef } from 'react';

import { cn } from '@/lib/utils';

/** Six digits, everywhere. Both OTPs in the product are 6-digit codes (see the backend handlers). */
export const OTP_CODE_LENGTH = 6;

/**
 * The boxed one-time-code input (designer: `.sc-otp-verification__cell` / `__input`).
 *
 * LIFTED OUT OF `features/sessions/components/MobileVerificationGate.tsx`, where it lived as a
 * private component. Two more places now need the identical control — the Email Verification step's
 * applicant screen, and that step's preview in the workflow builder — and the builder lives in a
 * different feature, so importing it from sessions is not allowed (see
 * `.claude/rules/frontend/features.md`). A copy would have been the third implementation of
 * paste-fills-the-row and backspace-steps-back, which is exactly the drift this rule exists to
 * prevent.
 *
 * PRESENTATIONAL ONLY. The code stays as ONE string in the caller's state; this renders that string
 * across six inputs and maps typing back to it. Callers keep their own submit, length check and
 * resend logic.
 *
 * Every cell is `flex-1` with `aspect-square`, as the designer has it, so the row fits any phone
 * width instead of six fixed boxes that overflow a small screen.
 */
export function OtpCells({
  value,
  onChange,
  onComplete,
  disabled,
  length = OTP_CODE_LENGTH,
}: {
  value: string;
  onChange: (next: string) => void;
  /**
   * Fired with the COMPLETE code when the last digit lands.
   *
   * The value is passed explicitly because the caller's state has not re-rendered yet at this
   * point — reading it there would see one digit short and reject a valid code.
   */
  onComplete: (fullCode: string) => void;
  /** Blocks input while a verify call is in flight, so a code can't be edited mid-submit. */
  disabled?: boolean;
  length?: number;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  function setDigit(index: number, raw: string) {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return;

    // Handles paste as well as typing: dropping all six digits into any cell fills the row.
    const next = (value.slice(0, index) + digits + value.slice(index + digits.length))
      .replace(/\D/g, '')
      .slice(0, length);
    onChange(next);

    const focusAt = Math.min(index + digits.length, length - 1);
    refs.current[focusAt]?.focus();
    if (next.length === length) onComplete(next);
  }

  function onKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      // Clear this cell if it has a digit, otherwise step back and clear that one — the behaviour
      // people expect from a boxed code field.
      const target = value[index] ? index : Math.max(index - 1, 0);
      onChange((value.slice(0, target) + value.slice(target + 1)).slice(0, length));
      refs.current[target]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft') refs.current[Math.max(index - 1, 0)]?.focus();
    if (e.key === 'ArrowRight') refs.current[Math.min(index + 1, length - 1)]?.focus();
  }

  return (
    // Capped at 320px and centred. The cells are `flex-1` + `aspect-square`, so on the designer's
    // 388px phone they land at ~48px — but a `max-w-lg` (512px) card inflated the same rule to
    // ~78px squares that dominated the screen. The cap keeps them phone-sized on every width while
    // still shrinking on a narrow handset.
    <div className="mx-auto flex w-full max-w-[320px] flex-row items-stretch justify-center gap-2">
      {Array.from({ length }, (_, i) => i).map((i) => (
        <div key={i} className="aspect-square min-w-0 flex-1">
          <input
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            // Only the first cell claims the autofill, or the browser tries to put the whole code
            // into every box.
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            aria-label={`Digit ${i + 1} of ${length}`}
            value={value[i] ?? ''}
            disabled={disabled}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onFocus={(e) => e.currentTarget.select()}
            className={cn(
              'block h-full w-full min-w-0 appearance-none rounded-lg border-[1.5px] bg-white p-0 text-center text-[18px] font-semibold leading-none text-[#111827] outline-none transition-colors',
              value[i] ? 'border-[#0048FF]' : 'border-[#D1D5DB]',
              'focus:border-[#0048FF]',
              disabled && 'opacity-60',
            )}
          />
        </div>
      ))}
    </div>
  );
}
